import { BASE_LINK_COLOR } from 'HubStyleTokens/theme';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIModal from 'UIComponents/dialog/UIModal';
import { Text } from 'foundations-components/components/Text';
import UICaret from 'UIComponents/icon/icons/UICaret';
import UISearchInput from 'UIComponents/input/UISearchInput';
import useLocale from 'developer-docs-shared-components/hooks/useLocale';
import { useViewport } from 'developer-docs-shared-components/hooks/useViewport';
import { useDebounce } from 'developer-docs-shared-components/modules/Doc/hooks/useDebounce';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useHistory, withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { selectSearchQuery, selectSearchResults, selectSearchStatus } from '../../../selectors/searchState';
import { Paths } from '../../RoutesPublic';
import { CALYPSO_DARK, EERIE, GREAT_WHITE, KOALA } from 'HubStyleTokens/colors';
import UIButton from 'UIComponents/button/UIButton';
import UIForm from 'UIComponents/form/UIForm';
import UIFormControl from 'UIComponents/form/UIFormControl';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIScreenReaderOnly from 'UIComponents/text/UIScreenReaderOnly';
// @ts-expect-error No types given for file

import styled from 'styled-components';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import { clearSearch } from '../../../actions/SearchActions';
import { SEARCH, SEARCH_ACTIONS, SEARCH_ORIGINS } from '../../../tracking/trackerActions';
import { getUsageTracker } from '../../../utils/usage-tracker/index';
import NoResultsFound from '../NoResultsFound';
import SearchError from '../SearchError';
import { DEBOUNCED_SEARCH_INPUT_TIME, MAX_SEARCH_DROPDOWN_RESULTS, MIN_TYPE_TO_SEARCH_QUERY_LENGTH, SEARCH_DROPDOWN_DESKTOP_WIDTH } from '../constants';
import ResultOption from '../dropdownSearch/ResultOption';
import { getSearchResultsPageUrl, searchSanitizedAndEncodedQuery } from '../searchHelpers';
import DropdownEmptyState from './DropdownEmptyState';
import { ECLIPSE } from 'developer-docs-shared-components/config/cssConstants';
import { useKeyboardShortcut } from '../hooks/useKeyboardShortcut';
import UIFlex from 'UIComponents/layout/UIFlex';
import { View } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const StyledLinkText = styled(FormattedMessage).withConfig({
  displayName: "DropdownSearchInput__StyledLinkText",
  componentId: "cwt2qb-0"
})(["font-size:14px;font-weight:400;margin-right:16px;"]);
const SearchButton = styled(UIButton).withConfig({
  displayName: "DropdownSearchInput__SearchButton",
  componentId: "cwt2qb-1"
})(["&&{display:flex;justify-content:space-between;align-items:center;color:", ";&:hover{text-decoration:none;color:", ";}}"], EERIE, EERIE);
const SearchHeader = styled(UIDialogHeader).withConfig({
  displayName: "DropdownSearchInput__SearchHeader",
  componentId: "cwt2qb-2"
})(["&&{background:", ";}"], ECLIPSE);
const CMDKIcon = styled.div.withConfig({
  displayName: "DropdownSearchInput__CMDKIcon",
  componentId: "cwt2qb-3"
})(["&&{background-color:", ";border:1px solid ", ";font-size:12px;margin-right:12px;line-height:18px;padding:0px 2px;color:", ";}"], KOALA, GREAT_WHITE, EERIE);
const DropdownSearchInput = ({
  searchInput,
  dropdownSearchFocused,
  setSearchInput,
  query,
  searchResults,
  loadingStatus
}) => {
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const dispatch = useDispatch();
  const {
    isDesktop,
    isBelowLaptop
  } = useViewport();
  const menuWidth = useMemo(() => isDesktop ? `${SEARCH_DROPDOWN_DESKTOP_WIDTH}px` : undefined, [isDesktop]);
  const debouncedSearchInput = useDebounce(searchInput, DEBOUNCED_SEARCH_INPUT_TIME);
  const showEmptyState = useMemo(() => loadingStatus.failed || searchResults.length === 0, [loadingStatus, searchResults]);
  const locale = useLocale();
  useEffect(() => {
    if (debouncedSearchInput.length < MIN_TYPE_TO_SEARCH_QUERY_LENGTH && query) dispatch(clearSearch());
  }, [debouncedSearchInput, query, dispatch]);

  // Handles for search while typing:
  useEffect(() => {
    if (debouncedSearchInput.length >= MIN_TYPE_TO_SEARCH_QUERY_LENGTH) {
      searchSanitizedAndEncodedQuery(debouncedSearchInput, locale, dispatch, true);
    }
  }, [debouncedSearchInput, dropdownSearchFocused, dispatch, locale]);
  const results = useMemo(() => {
    if (showEmptyState) return [];
    return searchResults.slice(0, MAX_SEARCH_DROPDOWN_RESULTS);
  }, [showEmptyState, searchResults]);
  const searchPageUrl = useMemo(() => {
    return getSearchResultsPageUrl(searchInput);
  }, [searchInput]);
  const trackNavigateToSearchResultsPage = useCallback(() => {
    getUsageTracker().track(SEARCH, {
      action: SEARCH_ACTIONS.REDIRECT_TO_RESULTS_PAGE,
      origin: SEARCH_ORIGINS.DROPDOWN_SEARCH,
      searchTerm: searchInput
    });
  }, [searchInput]);
  const handleSearch = evt => {
    evt.preventDefault();
    trackNavigateToSearchResultsPage();
    searchSanitizedAndEncodedQuery(debouncedSearchInput, locale, dispatch, true);
    history.push(searchPageUrl);
  };
  const onSearchInputChange = useCallback(evt => {
    if (evt.currentTarget.value === '' && searchInput !== '') {
      getUsageTracker().track(SEARCH, {
        action: SEARCH_ACTIONS.SEARCH_CLEARED,
        origin: SEARCH_ORIGINS.DROPDOWN_SEARCH
      });
    }
    setSearchInput(evt.currentTarget.value);
  }, [searchInput, setSearchInput]);
  const emptyState = useMemo(() => {
    if (loadingStatus.failed) {
      return /*#__PURE__*/_jsx(SearchError, {});
    }
    if (loadingStatus.succeeded && searchResults.length === 0) {
      return /*#__PURE__*/_jsx(NoResultsFound, {
        inDropdown: true
      });
    }
    return /*#__PURE__*/_jsx(DropdownEmptyState, {});
  }, [loadingStatus, searchResults]);
  const onExitSearch = useCallback(() => {
    setIsModalOpen(false);
    dispatch(clearSearch());
    setSearchInput('');
  }, [dispatch, setSearchInput]);
  useKeyboardShortcut(['Cmd+K', '/'], () => {
    setIsModalOpen(true);
  });
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsxs(SearchButton, {
      "data-test-id": "search-input-for-modal",
      use: isBelowLaptop ? 'transparent' : 'form',
      onClick: () => {
        setIsModalOpen(true);
      },
      width: menuWidth,
      children: [!isBelowLaptop && /*#__PURE__*/_jsx(FormattedMessage, {
        message: "developer-docs-ui.header.search.placeholder"
      }), /*#__PURE__*/_jsxs(UIFlex, {
        justify: "end",
        align: "center",
        children: [!isBelowLaptop && /*#__PURE__*/_jsx(CMDKIcon, {
          children: "CMD+K"
        }), /*#__PURE__*/_jsx(UIIcon, {
          name: "search",
          color: isBelowLaptop ? EERIE : CALYPSO_DARK,
          screenReaderText: I18n.text('developer-docs-ui.header.search.abbreviatedPlaceholder')
        })]
      })]
    }), isModalOpen && /*#__PURE__*/_jsxs(UIModal, {
      onEsc: onExitSearch,
      width: 950,
      "data-test-id": "search-modal",
      children: [/*#__PURE__*/_jsxs(SearchHeader, {
        children: [/*#__PURE__*/_jsx(Text, {
          as: "h2",
          color: "text-core-on-fill-default",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "developer-docs-ui.header.search.abbreviatedPlaceholder"
          })
        }), /*#__PURE__*/_jsx(UIDialogCloseButton, {
          onClick: onExitSearch
        })]
      }), /*#__PURE__*/_jsxs("div", {
        className: "p-y-9 p-x-10 flex-column",
        children: [/*#__PURE__*/_jsxs(UIForm, {
          role: "search",
          onSubmit: handleSearch,
          action: Paths.SEARCH,
          children: [/*#__PURE__*/_jsx(UIFormControl, {
            "aria-label": I18n.text('developer-docs-ui.header.search.abbreviatedPlaceholder'),
            children: /*#__PURE__*/_jsx(UISearchInput, {
              "data-test-id": "modal-search-input",
              autoFocus: true,
              placeholder: I18n.text('developer-docs-ui.header.search.abbreviatedPlaceholder'),
              value: searchInput,
              onChange: onSearchInputChange
            })
          }), /*#__PURE__*/_jsx(UIScreenReaderOnly, {
            children: /*#__PURE__*/_jsx(UIButton, {
              type: "submit",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "developer-docs-ui.header.search.submit"
              })
            })
          })]
        }), results.length > 0 ? /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(View, {
            "data-test-id": "modal-search-results",
            paddingBlock: "100",
            as: "div",
            children: results.map((result, i) => /*#__PURE__*/_jsx(ResultOption, {
              result: result,
              exitSearch: onExitSearch
            }, `${result.title}-${i}`))
          }), /*#__PURE__*/_jsxs(UIRouterLink, {
            "aria-label": I18n.text('developer-docs-ui.search.resultsPopover.submit_aria'),
            to: searchPageUrl,
            onClick: trackNavigateToSearchResultsPage,
            "data-test-id": "see-more-results-link",
            className: "align-self-end align-center",
            children: [/*#__PURE__*/_jsx(StyledLinkText, {
              message: "developer-docs-ui.search.resultsPopover.seeMore"
            }), /*#__PURE__*/_jsx(UICaret, {
              color: BASE_LINK_COLOR,
              height: 10
            })]
          })]
        }) : emptyState]
      })]
    })]
  });
};
const mapStateToProps = state => ({
  query: selectSearchQuery(state),
  searchResults: selectSearchResults(state),
  loadingStatus: selectSearchStatus(state)
});

/** Used by the header when not on the Search Results Page */
export default compose(withRouter, connect(mapStateToProps))(DropdownSearchInput);